"use client";

import { useRef } from "react";
import Image from "next/image";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { GroqImage } from "@/helpers/Image";
import { CTAButton } from "@/shadcn/ui/button";
import { competitorRichText } from "./richtext";
import { CompetitorWhyChangeCard, PageCompetitorSchema } from "@/sanity/pages/PageCompetitorTemplate";
import dots from "@/public/home-v2/why-iq/dots-texture.jpg";

const ease = [0.25, 0.1, 0.25, 1] as const;

function DotsTexture() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute top-[-28.6875rem] left-[27.6875rem] flex h-[88.375rem] w-[85.875rem] items-center justify-center opacity-20 mix-blend-exclusion">
      <div className="relative h-[85.875rem] w-[88.375rem] shrink-0 rotate-90">
        <Image src={dots} alt="" fill sizes="45vw" className="object-cover" />
        <div className="absolute inset-0 bg-[linear-gradient(0deg,#000_0%,rgba(0,0,0,0)_59.886%)]" />
      </div>
    </div>
  );
}

function WhyChangeCard({ card }: { card: CompetitorWhyChangeCard }) {
  return (
    <div className="relative flex w-full flex-col gap-[4rem] overflow-hidden border border-[rgba(47,47,47,0.1)] bg-white p-[2.5rem] lg:gap-[5rem] lg:p-[3rem]">
      {card.textured && <DotsTexture />}
      <div className="relative flex h-[7rem] w-[7rem] shrink-0 items-center justify-center rounded-[0.75rem] bg-[rgba(0,27,20,0.05)] p-[1rem]">
        {card.icon && <GroqImage src={card.icon} alt="" quality={100} className="!h-auto !w-[3rem] object-contain" />}
      </div>
      <div className="relative flex flex-col gap-[1rem] text-[#001b14]">
        {card.title && <p className="mb-0 font-geist text-[3rem] font-medium leading-[1.3]">{card.title}</p>}
        {card.body && <p className="mb-0 font-geist text-[2.25rem] leading-[1.5] opacity-80">{card.body}</p>}
      </div>
    </div>
  );
}

export default function WhyChangeSection({ data }: { data: PageCompetitorSchema }) {
  const cards = data.whyChangeCards ?? [];
  const cardsRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: cardsRef, offset: ["start center", "end center"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 200, damping: 40, restDelta: 0.001 });
  const fillY = useTransform(smooth, [0, 1], [0, 1]);
  const markerY = useTransform(smooth, [0, 1], ["0%", "100%"]);

  if (!data.whyChangeTitle && !cards.length) return null;

  return (
    <section className="w-full bg-[#fbfbfb] px-[2rem] [text-wrap:pretty] pt-[7rem] pb-[8rem] lg:px-0 lg:pt-0 lg:pb-[15rem]">
      <div className="mx-auto flex w-[min(150.75rem,100%)] flex-col gap-[3.375rem] lg:flex-row lg:items-start lg:justify-between lg:gap-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease }}
          className="flex w-full flex-col gap-[5rem] max-lg:items-center max-lg:text-center lg:sticky lg:top-0 lg:w-[min(68.375rem,100%)] lg:shrink-0 lg:pt-[8rem] lg:pb-[4rem]">
          <div className="flex w-[min(41.5rem,100%)] flex-col gap-[2.25rem] max-lg:items-center lg:w-[min(60rem,100%)]">
            <div className="flex flex-col gap-[1.25rem]">
              {data.whyChangeChip && (
                <p className="mb-0 font-blacker text-[1.875rem] leading-[1.5] text-black italic">{data.whyChangeChip}</p>
              )}
              {data.whyChangeTitle && (
                <div className="richtext gap-[1.625rem] max-lg:[text-wrap:balance] lg:richtext-start lg:gap-[2.25rem] lg:items-start lg:text-left prose-headings:my-0 prose-headings:font-geist prose-headings:font-medium max-lg:prose-headings:leading-[1.15] lg:prose-headings:leading-none prose-headings:text-black max-lg:prose-headings:text-[4.5rem] max-lg:prose-headings:tracking-[-0.225rem] lg:prose-headings:w-[min(60.125rem,100%)] lg:prose-headings:text-[7rem] lg:prose-headings:tracking-[-0.35rem] prose-p:my-0 prose-p:font-geist prose-p:!text-[2.25rem] prose-p:leading-[1.5] prose-p:!text-black/70 [--hl-wash:4.5rem] lg:[--hl-wash:5.75rem] [--hl-tracking:-0.03em] [--hl-ml:1.375rem] [&_:is(h1,h2)_span]:leading-[0]">
                  <PortableText value={data.whyChangeTitle} components={competitorRichText} />
                </div>
              )}
            </div>
          </div>
          {data.whyChangeCTA && (
            <CTAButton data={data.whyChangeCTA} className="lg:w-[min(34.75rem,100%)] [&_.button]:min-h-[5.875rem] max-lg:hidden" />
          )}
        </motion.div>

        <div className="flex w-full items-start gap-[7rem] lg:w-auto lg:pt-[8rem]">
          <div aria-hidden className="relative hidden w-0 shrink-0 self-stretch lg:block">
            <div className="absolute inset-y-0 left-[-0.09375rem] w-[0.1875rem] bg-black/[0.12]" />
            <motion.div
              className="absolute top-0 left-[-0.09375rem] h-full w-[0.1875rem] origin-top bg-[#228e71]"
              style={{ scaleY: fillY }}
            />
            <motion.div
              className="absolute left-0 -translate-x-1/2 -translate-y-1/2 bg-[rgba(202,223,217,0.37)] p-[0.75rem] backdrop-blur-[0.5rem]"
              style={{ top: markerY }}>
              <div className="h-[1.21875rem] w-[1.21875rem] bg-[#228e71]" />
            </motion.div>
          </div>

          <div ref={cardsRef} className="flex w-full flex-col gap-[1.5rem] lg:w-[min(67.25rem,100%)] lg:gap-[2.25rem]">
            {cards.map((card, index) => (
              <motion.div
                key={card._key ?? index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease }}>
                <WhyChangeCard card={card} />
              </motion.div>
            ))}
          </div>
        </div>

        {data.whyChangeCTA && (
          <CTAButton data={data.whyChangeCTA} className="w-full [&_.button]:min-h-[5.875rem] lg:hidden" />
        )}
      </div>
    </section>
  );
}
